import { useState, useEffect, useCallback } from "react";
import { Trash } from "lucide-react";
import { Dialog } from "../../../../../../components/dialog";
import GenericTable from "../../../../../../components/table";
import TooltipHorizon from "../../../../../../components/tooltip";
import Loader from "../../../../../../components/Loader";
import toast from "react-hot-toast";
import { useConfirmation } from "../../../../../../components/dialog/confirmation";
import { useAgentInfo } from "../../../../../../layouts/agent/agent-info/_context/AgentInfoContext";
import { 
  getAgentGuidances, 
  createAgentGuidance, 
  updateAgentGuidance, 
  deleteAgentGuidance 
} from "../../../../../../api/axios";
import { AgentGuidance } from "../../../../../../types";
import dayjs from "dayjs";
import { DATE_TIME_FORMAT } from "../../../../../../constants/date";
import { useTranslation } from "react-i18next";

const GuidancesTab = () => {
  const { t } = useTranslation();
  const { agentInfo } = useAgentInfo();
  const [guidances, setGuidances] = useState<AgentGuidance[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedGuidance, setSelectedGuidance] = useState<AgentGuidance | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });

  const { confirm } = useConfirmation();

  const fetchGuidances = useCallback(async (signal?: AbortSignal) => {
    if (!agentInfo?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await getAgentGuidances(agentInfo.id, {
        skip: 0,
        take: 999, // Get more items for better UX
        order_by: 'created_at',
        order_direction: 'desc'
      });
      
      setGuidances(response.items || []);
    } catch (err: any) {
      if (signal?.aborted || err.name === "CanceledError") {
        return;
      }
      setError("Click here to retry");
      setGuidances([]);
      toast.error("Failed to fetch guidances");
    } finally {
      setLoading(false);
    }
  }, [agentInfo?.id]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchGuidances(abortController.signal);

    return () => abortController.abort();
  }, [fetchGuidances]);

  const handleCreateGuidance = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agentInfo?.id) {
      toast.error("Agent information not available");
      return;
    }
    
    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error("Please fill in both name and description");
      return;
    }

    setIsCreating(true);
    try {
      await createAgentGuidance(agentInfo.id, {
        name: formData.name.trim(),
        description: formData.description.trim()
      });
      
      toast.success("Guidance created successfully");
      setFormData({ name: "", description: "" });
      setIsCreateDialogOpen(false);
      await fetchGuidances();
    } catch (err) {
      toast.error("Failed to create guidance");
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateGuidance = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedGuidance || !agentInfo?.id || !formData.name.trim() || !formData.description.trim()) {
      toast.error("Please fill in both name and description");
      return;
    }

    setIsUpdating(true);
    try {
      await updateAgentGuidance(agentInfo.id, selectedGuidance.id, {
        name: formData.name.trim(),
        description: formData.description.trim()
      });
      
      toast.success("Guidance updated successfully");
      await fetchGuidances();
      setIsEditDialogOpen(false);
      setSelectedGuidance(null);
      setFormData({ name: "", description: "" });
    } catch (err) {
      toast.error("Failed to update guidance");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteGuidance = async (guidanceId: string) => {
    if (!agentInfo?.id) {
      toast.error("Agent information not available");
      return;
    }

    setUpdatingRowId(guidanceId);
    try {
      await deleteAgentGuidance(agentInfo.id, guidanceId);
      
      toast.success("Guidance deleted successfully");
      await fetchGuidances();
    } catch (err) {
      toast.error("Failed to delete guidance");
    } finally {
      setUpdatingRowId(null);
    }
  };

  const handleDeleteWithConfirmation = async (guidanceId: string, guidanceName: string) => {
    const confirmed = await confirm({
      title: "Delete guidance",
      message: `Are you sure you want to delete "${guidanceName}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });
    
    if (confirmed) {
      await handleDeleteGuidance(guidanceId);
    }
  };


  const handleEditGuidance = (guidance: AgentGuidance) => {
    setSelectedGuidance(guidance);
    setFormData({
      name: guidance.name,
      description: guidance.description,
    });
    setIsEditDialogOpen(true);
  };

  // Check if form has changes
  const hasChanges = selectedGuidance && (
    formData.name !== selectedGuidance.name || 
    formData.description !== selectedGuidance.description
  );

  const handleCancel = () => {
    setFormData({ name: "", description: "" });
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
    setSelectedGuidance(null);
  };

  // Define columns for GenericTable
  const columns = [
    {
      accessorKey: "name",
      header: () => <p>{t("common.name")}</p>,
      cell: (info: any) => (
        <p className="max-w-xs truncate text-sm text-navy-700 dark:text-white">
          {info.getValue() || t("library.noNameProvided")}
        </p>
      ),
    },
    {
      accessorKey: "description",
      header: () => <p>{t("common.description")}</p>,
      cell: (info: any) => (
        <p className="max-w-xs truncate text-sm text-navy-700 dark:text-white">
          {info.getValue() || t("library.noDescriptionProvided")}
        </p>
      ),
    },
    {
      accessorKey: "created_at",
      header: () => <p>{t("library.createdDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {info.getValue() ? dayjs(info.getValue()).format(DATE_TIME_FORMAT) : t("library.unknown")}
        </p>
      ),
    },
    {
      accessorKey: "updated_at",
      header: () => <p>{t("library.updatedDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {info.getValue() ? dayjs(info.getValue()).format(DATE_TIME_FORMAT) : t("library.unknown")}
        </p>
      ),
    },
    {
      accessorKey: "actions",
      header: () => <p>{t("library.actions")}</p>,
      cell: (info: any) => {
        const guidance = info.row.original as AgentGuidance;
        return (
          <div className="flex items-center gap-4">
            <TooltipHorizon
              trigger={
                <button
                  disabled={updatingRowId === guidance.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteWithConfirmation(guidance.id, guidance.name);
                  }}
                  className="rounded-md p-2 text-red-500 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900"
                >
                  <Trash size={16} />
                </button>
              }
              content={<div>Delete guidance</div>}
              placement="top"
              extra=""
            />
          </div>
        );
      },
    },
  ];

  const dataWithActions = guidances.map((row) => ({
    ...row,
    updatingRowId,
  }));

  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return updatingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  const handleRowClick = (guidance: AgentGuidance) => {
    handleEditGuidance(guidance);
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader txt={t("library.loadingGuidances")} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div
          className="cursor-pointer text-red-500 hover:text-red-700"
          onClick={() => fetchGuidances()}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <GenericTable
        data={dataWithActions}
        columns={columns as any}
        searchPlaceholder={t("library.searchGuidances")}
        addLabel={t("library.addGuidance")}
        renderRowClassName={renderRowClassName}
        onAdd={() => setIsCreateDialogOpen(true)}
        onRowClick={handleRowClick}
      />

      {/* Create Guidance Dialog */}
      <Dialog
        isOpen={isCreateDialogOpen}
        setIsOpen={setIsCreateDialogOpen}
        onClose={handleCancel}
        header={t("library.addNewGuidance")}
        className="w-full max-w-md"
      >
        <form onSubmit={handleCreateGuidance} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("common.name")} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t("library.enterGuidanceName")}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              required
            />
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("common.description")} *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t("library.enterGuidanceDescription")}
              rows={4}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              required
            />
          </div>
          
          <div className="pt-4">
            <button
              type="submit"
              disabled={isCreating || !formData.name.trim() || !formData.description.trim()}
              className="w-full rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCreating ? t("common.creating") : t("library.createGuidance")}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Edit Guidance Dialog */}
      <Dialog
        isOpen={isEditDialogOpen}
        setIsOpen={setIsEditDialogOpen}
        onClose={handleCancel}
        header={t("library.editGuidance")}
        className="w-full max-w-md"
      >
        <form onSubmit={handleUpdateGuidance} className="space-y-4">
          <div>
            <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("common.name")} *
            </label>
            <input
              type="text"
              id="edit-name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t("library.enterGuidanceName")}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              required
            />
          </div>
          
          <div>
            <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("common.description")} *
            </label>
            <textarea
              id="edit-description"
              name="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t("library.enterGuidanceDescription")}
              rows={4}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              required
            />
          </div>
          
          <div className="pt-4">
            <button
              type="submit"
              disabled={isUpdating || !formData.name.trim() || !formData.description.trim() || !hasChanges}
              className="w-full rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUpdating ? t("common.saving") : t("common.saveChanges")}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default GuidancesTab;
